/**
 * TourRig — real guided-tour camera rig (Plan 05-03 / NAV-05).
 *
 * Replaces the Phase 3 null stub. Drives the camera along a manifest-authored
 * CatmullRomCurve3 at arc-length-uniform speed, firing caption events as the
 * normalized progress `t` crosses each step.triggerAtT threshold. At t=1 the
 * rig dispatches {type:'EXIT_TOUR'} via useNavSend exactly once and the
 * machine returns to freeWalk.
 *
 * Contract (mirrors StreetcarRig 05-01 shape):
 *   - Reads activeTourId from useTourStore; manifest from useManifest. If
 *     either is absent, returns null (no rig mounted).
 *   - Builds the curve via buildTourCurve(tour) inside useMemo keyed on
 *     tour?.id so mid-flight activeTourId changes rebuild cleanly.
 *   - Rides at arc-length-uniform speed via curve.getPointAt(t) — NEVER
 *     curve.getPoint(t) (Pitfall 1 closure).
 *   - Orients via camera.lookAt(curve.getPointAt(t + 0.01)) — look-ahead
 *     point, NOT tangent vector (Pitfall 2 closure).
 *   - Caption fires: findLastIndex of steps where triggerAtT <= t; if that
 *     index is new and >= 0, calls useTourStore.setActiveCaption exactly
 *     once per step (lastCaptionIdxRef guard).
 *   - EXIT_TOUR fires once at t=1 (exitedRef guard) then the rig continues
 *     returning until the machine transitions state and NavRig unmounts it.
 *
 * Reset behavior:
 *   - When activeTourId changes (e.g., REBOARD fires and another tour is
 *     picked later), elapsedRef/lastCaptionIdxRef/exitedRef reset via
 *     useEffect keyed on activeTourId.
 *
 * Writes pose to useNavStore.setCameraPose every frame so Minimap /
 * DebugHUD can observe mid-tour position.
 */
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useManifest } from '../content/useManifest';
import { useNavSend } from './NavContext';
import { useNavStore } from '../store/useNavStore';
import { useTourStore } from '../store/useTourStore';
import { buildTourCurve } from './tourSpline';

export function TourRig() {
  const { camera } = useThree();
  const send = useNavSend();
  const { manifest } = useManifest();
  const activeTourId = useTourStore((s) => s.activeTourId);

  // Resolve the tour object from the manifest by id.
  const tour = useMemo(() => {
    if (!manifest || !activeTourId) return null;
    return manifest.tours.find((t) => t.id === activeTourId) ?? null;
  }, [manifest, activeTourId]);

  // Build curve once per tour id change (useMemo keyed on tour identity).
  // If tour is null, curve is null and the rig short-circuits.
  const curve = useMemo(() => {
    if (!tour) return null;
    return buildTourCurve(tour);
  }, [tour]);

  // Accumulated elapsed time — uses R3F delta (seconds) so tests can drive it
  // via advanceFrames() with a controlled clock. Matches StreetcarRig pattern.
  const elapsedRef = useRef(0);
  // Which step index was most recently fired. -1 means "no caption yet."
  const lastCaptionIdxRef = useRef(-1);
  // One-shot guard so EXIT_TOUR dispatches exactly once per tour completion.
  const exitedRef = useRef(false);
  // Reusable scratch Vector3 for lookAt — avoids per-frame allocation.
  const lookAtScratch = useRef(new THREE.Vector3());

  // Reset all per-ride state whenever activeTourId changes (rig remount or
  // tour swap mid-flight). Keyed on activeTourId, not `tour`, so a manifest
  // hot-reload that does NOT change the id is a no-op here.
  useEffect(() => {
    elapsedRef.current = 0;
    lastCaptionIdxRef.current = -1;
    exitedRef.current = false;
  }, [activeTourId]);

  useFrame((_state, delta) => {
    if (!curve || !tour) return;

    elapsedRef.current += delta;
    const elapsed = elapsedRef.current;
    const t = Math.min(elapsed / tour.durationS, 1);

    // Arc-length-uniform position (Pitfall 1 closure — getPointAt not getPoint)
    const pos = curve.getPointAt(t);
    camera.position.copy(pos);

    // Look-ahead-point orientation (Pitfall 2 closure — NOT tangent)
    const lookT = Math.min(t + 0.01, 1);
    lookAtScratch.current.copy(curve.getPointAt(lookT));
    camera.lookAt(lookAtScratch.current);

    // Persist pose every frame for Minimap / DebugHUD observers
    useNavStore.getState().setCameraPose(
      [pos.x, pos.y, pos.z],
      [
        camera.quaternion.x,
        camera.quaternion.y,
        camera.quaternion.z,
        camera.quaternion.w,
      ],
    );

    // Caption-firing: find highest-index step whose triggerAtT <= t.
    // Iterate from last → first so the first match IS the highest index.
    // Avoids Array.prototype.findLastIndex (ES2023, target is ES2022).
    // n <= ~10 in practice (manifest tours).
    let idx = -1;
    for (let i = tour.steps.length - 1; i >= 0; i--) {
      if (t >= tour.steps[i].triggerAtT) {
        idx = i;
        break;
      }
    }
    if (idx !== lastCaptionIdxRef.current && idx >= 0) {
      useTourStore.getState().setActiveCaption(tour.steps[idx].caption);
      lastCaptionIdxRef.current = idx;
    }

    // EXIT_TOUR at t=1 — send once, via useNavSend. The machine will
    // transition guidedTour → freeWalk and NavRig will unmount this rig.
    if (t >= 1 && !exitedRef.current) {
      exitedRef.current = true;
      send({ type: 'EXIT_TOUR' });
    }
  });

  return null; // rig is a pure side-effect on the R3F camera
}
